import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import { useSeenHint, SEEN_HINT } from '../../../utils/seenHints';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';

/**
 * Partner Workspace › My Schools — the floating "?" button and its five-step
 * guide (bug deck LA-PW-07-Oct-03, slides 18-23; Ian's HTML mockup
 * "PbDQ_PW_My Schools_Pop-up_Guidance", 11 Oct 2026).
 *
 * Colours go through the --brand-* variables (light value as the fallback) so
 * the pop-up follows dark mode.
 *
 * Titles, body text and tips are the mockup's, word for word. Each step shows
 * the row's five buttons with the one(s) to press ringed and the rest faded.
 */
const ORANGE = '#FF6A13';
const ORANGE_DEEP = '#F2620F';
const SEEN_KEY = 'pw_my_schools_guide_seen';

const ACTIONS = ['Edit Details', 'Publish Workspace', 'Link Product', 'Link Programs', 'Assign Licenses & Credits'];

const STEPS = [
    {
        hi: [],
        title: 'Add a School or Network',
        body: 'Start by adding a school network or a school. You’ll set up its workspace in the next steps.',
        tip: 'New entries are saved as drafts, and their workspaces aren’t activated yet.',
    },
    {
        hi: [0],
        title: 'Edit Details',
        body: 'Review the network or school’s details and make sure the name, contact name and contact email are correct. The contact you enter becomes the owner of the workspace.',
        tip: 'Check the contact email carefully, since the activation email is sent to this address.',
    },
    {
        hi: [1],
        title: 'Publish Workspace',
        body: 'Publish the workspace once the details are confirmed. The contact receives a workspace activation email and can then sign in to their workspace.',
        tip: 'Schools within a network aren’t published with it. The network publishes them from its own workspace.',
    },
    {
        hi: [2, 3],
        title: 'Link Product and Link Programs',
        body: 'Link LearnCreator so the network or school can build its own learning programs in its workspace. Link programs to deliver assessments and learning programs as part of the solution you provide, including the COS Children Assessment and programs you’ve created.',
        tip: 'Linked items can’t be used until licenses and credits are assigned.',
    },
    {
        hi: [4],
        title: 'Assign Licenses & Credits',
        body: 'Assign licenses to the network or school so its learners can take the linked assessments and programs, with one license needed per learner for each. Assign LearnCreator credits so it can build programs in LearnCreator, with one credit covering one session.',
        tip: 'Licenses and credits are purchased with DQ Tokens (DQT). Your DQT balance is shown at the bottom of the page.',
    },
];

/*
 * School Network Workspace (bug deck Ian-SNW-07Oct2026-1, slides 50-55; updated
 * by Ian-SNW-19Oct-2 to the mockup "PbDQ_SNW_My Schools_New Guidance"). Six
 * steps, each ringing either one of the two filled header buttons or button(s)
 * in a school row's five, laid out as on the page.
 */
const NETWORK_SEEN_KEY = 'snw_my_schools_guide_seen';
const NETWORK_BAR = ['Publish Workspace', '+ Add a School'];
const NETWORK_ROW = ['Edit Details', 'Assign Licenses', 'Link Product', 'Link Programs', 'Manage Classes'];
const NETWORK_STEPS = [
    {
        bar: 1, title: 'Add a School',
        body: 'Start by adding the schools in your network. You’ll set up each school’s workspace in the next steps.',
        tip: 'New schools are saved as drafts, and their workspaces aren’t activated yet.',
    },
    {
        row: [0], title: 'Edit Details',
        body: 'Review each school’s details, whether they were added by your partner or by you. Make sure the name, contact name and contact email are correct. The contact you enter becomes the owner of the school’s workspace.',
        tip: 'Check the contact email carefully, since the activation email is sent to this address.',
    },
    {
        bar: 0, title: 'Publish Workspace',
        body: 'Publish school workspaces once their details are confirmed. Each school’s contact receives a workspace activation email and can then sign in to their workspace.',
        tip: 'Once published, a workspace can’t be unpublished.',
    },
    {
        row: [2, 3], title: 'Link Product and Link Programs',
        body: 'Link LearnCreator so the school can build its own learning programs in its workspace. Link programs to deliver assessments and learning programs as part of the solution you provide, including the COS Children Assessment and programs you’ve created.',
        tip: 'Linked items can’t be used until licenses and credits are assigned.',
    },
    {
        row: [1], title: 'Assign Licenses',
        body: 'Assign licenses to the school so its learners can take the linked assessments and programs, with one license needed per learner for each.',
        tip: 'Your total and remaining licenses are shown in the summary at the top of the page.',
    },
    {
        row: [4], title: 'Manage Classes',
        body: 'Create classes for the school with their teacher, learner and parent details, and view or manage the school’s existing classes.',
        tip: 'My Classes is also available anytime from the sidebar.',
    },
];

/** The network guide's picture: the header buttons, or one school row. */
const NetworkShot = ({ cur }) => (cur.bar !== undefined ? (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '26px' }}>
        <Typography component="span" sx={{ fontSize: 24, fontWeight: 700, textAlign: 'center', opacity: 0.55 }}>My Schools</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <Box sx={{ width: 150, minWidth: 0, flexShrink: 1, height: 36, border: '1px solid var(--brand-border, #ddd)', borderRadius: '3px', backgroundColor: 'var(--brand-surface, #fff)', display: 'flex', alignItems: 'center', px: '12px', color: '#aaa', fontSize: 12, opacity: 0.45, overflow: 'hidden' }}>
                Search schools...
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap', height: 30, px: '14px', display: 'flex', alignItems: 'center', borderRadius: '15px', border: `1.5px solid ${ORANGE_DEEP}`, backgroundColor: 'var(--brand-surface, #fff)', color: ORANGE_DEEP, fontSize: 11, fontWeight: 700, opacity: 0.35 }}>
                Export School Details
            </Box>
            {NETWORK_BAR.map((label, i) => (
                <Box key={label} component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap', height: 30, width: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '15px', backgroundColor: ORANGE, color: '#fff', fontSize: 11, fontWeight: 700, opacity: i === cur.bar ? 1 : 0.35, boxShadow: i === cur.bar ? RING : 'none' }}>
                    {label}
                </Box>
            ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', opacity: 0.35 }}>
            {[0, 1, 2, 3].map((k) => <Box key={k} sx={{ height: 54, backgroundColor: 'var(--brand-surface, #fff)', border: '1px solid var(--brand-border, #f1e9e4)', borderRadius: '8px' }} />)}
        </Box>
    </Box>
) : (
    <Box sx={{ width: '100%', backgroundColor: 'var(--brand-surface, #fff)', border: '1px solid var(--brand-border, #f1e9e4)', borderRadius: '10px', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1.1fr 330px' }, alignItems: 'center', gap: '16px', p: '20px 22px', boxSizing: 'border-box' }}>
        <Typography component="span" sx={{ fontSize: 15, fontWeight: 500, opacity: 0.55 }}>School name</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', opacity: 0.55 }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 700 }}>Contact name</Typography>
            <Typography component="span" sx={{ fontSize: 13, color: ORANGE_DEEP }}>Contact email</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {NETWORK_ROW.map((label, i) => (
                <Box key={label} component="span" sx={{ gridColumn: label === 'Manage Classes' ? '1 / -1' : 'auto', height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${ORANGE_DEEP}`, borderRadius: '16px', color: ORANGE_DEEP, fontSize: 12, fontWeight: 700, backgroundColor: 'var(--brand-surface, #fff)', opacity: cur.row.includes(i) ? 1 : 0.35, boxShadow: cur.row.includes(i) ? RING : 'none' }}>
                    {label}
                </Box>
            ))}
        </Box>
    </Box>
));
const RING = `0 0 0 4px var(--brand-surface, #fff), 0 0 0 7px ${ORANGE_DEEP}`;

const MySchoolsGuide = ({ network = false }) => {
    const steps = network ? NETWORK_STEPS : STEPS;
    const last = steps.length - 1;
    const seenKey = network ? NETWORK_SEEN_KEY : SEEN_KEY;
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);
    // The red dot on the "?" until this user has opened the guide once — kept
    // per user in the database (users.seen_hints), so on every device.
    const [seen, markSeen] = useSeenHint(network ? SEEN_HINT.SNW_MY_SCHOOLS_GUIDE : SEEN_HINT.PW_MY_SCHOOLS_GUIDE, seenKey);

    const openGuide = () => {
        setStep(0);
        setOpen(true);
        markSeen();
    };
    const closeGuide = () => setOpen(false);

    // Arrow keys move between steps, as in the mockup (Escape is the Dialog's own).
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'ArrowRight') setStep((s) => Math.min(last, s + 1));
            if (e.key === 'ArrowLeft') setStep((s) => Math.max(0, s - 1));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, last]);

    const cur = steps[step];

    return (
        <>
            <Box
                component="button"
                type="button"
                onClick={openGuide}
                title="Guide to My Schools"
                aria-label="Guide to My Schools"
                sx={{
                    position: 'fixed', right: 24, bottom: 24, zIndex: 30, width: 52, height: 52,
                    borderRadius: '50%', border: 'none', backgroundColor: ORANGE, color: '#fff',
                    fontWeight: 700, fontSize: 24, cursor: 'pointer',
                    boxShadow: '0 6px 20px rgba(242,98,15,.35)',
                }}
            >
                ?
                {!seen && (
                    <Box component="span" sx={{
                        position: 'absolute', top: 2, right: 2, width: 12, height: 12, borderRadius: '50%',
                        backgroundColor: '#E5322D', border: '2px solid #FDF9F7',
                    }} />
                )}
            </Box>

            <Dialog
                open={open}
                onClose={closeGuide}
                maxWidth={false}
                // Fits a short window without a scrollbar: the spacing and the picture shrink.
                PaperProps={{ sx: { width: 900, maxWidth: '100%', borderRadius: '18px', p: { xs: '20px', sm: '28px 44px 36px' }, boxSizing: 'border-box', '@media (max-height: 820px)': { p: '18px 32px 20px' } } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Typography component="span" sx={{ flexShrink: 0, fontSize: 24, fontWeight: 700, color: ORANGE_DEEP }}>
                            Step {step + 1} of {steps.length}
                        </Typography>
                        <Typography component="h2" sx={{ m: 0, fontSize: 24, fontWeight: 700 }}>{cur.title}</Typography>
                    </Box>
                    <IconButton onClick={closeGuide} title="Close" aria-label="Close" size="small" sx={{ backgroundColor: 'var(--brand-surface-muted, #f5efeb)' }}>
                        <CloseIcon style={{ fontSize: 20, color: 'var(--brand-text-muted, #555)' }} />
                    </IconButton>
                </Box>

                {/* The picture of where to click: the empty-state button for step 1,
                    then the row's five buttons with this step's ringed. */}
                <Box sx={{
                    mt: '24px', minHeight: 340, borderRadius: '12px', backgroundColor: 'var(--brand-surface-subtle, #FDF9F7)', border: '1px solid var(--brand-border, #eadfd8)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', p: '24px', boxSizing: 'border-box',
                    '@media (max-height: 820px)': { mt: '14px', minHeight: 220, p: '16px' },
                }}>
                    {network ? <NetworkShot cur={cur} /> : step === 0 ? (
                        <Box sx={{
                            width: '100%', minHeight: 290, '@media (max-height: 820px)': { minHeight: 186 }, boxSizing: 'border-box', backgroundColor: 'var(--brand-surface, #fff)',
                            border: '1px dashed #F6CFC0', borderRadius: '14px', display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', gap: '10px', p: '20px',
                        }}>
                            <Box sx={{ width: 42, height: 42, border: '1px dashed #eee', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.6 }}>
                                <HomeOutlinedIcon style={{ fontSize: 22, color: '#888' }} />
                            </Box>
                            <Typography sx={{ fontSize: 16, fontWeight: 700, opacity: 0.6 }}>No schools yet</Typography>
                            <Typography sx={{ maxWidth: 380, textAlign: 'center', fontSize: 12, lineHeight: 1.5, color: '#999', opacity: 0.7 }}>
                                Add a single school, or a network if the schools belong to one group. They&apos;ll get an activation email when you publish their workspace.
                            </Typography>
                            <Box component="span" sx={{
                                mt: '8px', height: 40, px: '24px', display: 'flex', alignItems: 'center', borderRadius: '20px',
                                backgroundColor: ORANGE, color: '#fff', fontSize: 14, fontWeight: 700,
                                boxShadow: `0 0 0 5px var(--brand-surface, #fff), 0 0 0 8px ${ORANGE_DEEP}`,
                            }}>
                                + Add a School or Network
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{
                            width: '100%', backgroundColor: 'var(--brand-surface, #fff)', border: '1px solid var(--brand-border, #f1e9e4)', borderRadius: '10px',
                            display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1.2fr 200px' }, alignItems: 'center',
                            gap: '16px', p: '16px 20px', boxSizing: 'border-box',
                        }}>
                            <Typography component="span" sx={{ fontSize: 15, fontWeight: 500, opacity: 0.55 }}>School or network name</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', opacity: 0.55 }}>
                                <Typography component="span" sx={{ fontSize: 14, fontWeight: 700 }}>Contact name</Typography>
                                <Typography component="span" sx={{ fontSize: 13, color: ORANGE_DEEP }}>Contact email</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                {ACTIONS.map((label, i) => {
                                    const on = cur.hi.includes(i);
                                    return (
                                        <Box key={label} component="span" sx={{
                                            width: 180, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            border: `1.5px solid ${ORANGE_DEEP}`, borderRadius: '15px', color: ORANGE_DEEP,
                                            fontSize: 12, fontWeight: 700, backgroundColor: 'var(--brand-surface, #fff)',
                                            opacity: on ? 1 : 0.35, boxShadow: on ? RING : 'none',
                                        }}>
                                            {label}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mt: '28px', minHeight: 150, '@media (max-height: 820px)': { gap: '10px', mt: '16px', minHeight: 104 } }}>
                    <Typography sx={{ m: 0, fontSize: 16, lineHeight: 1.65, color: 'var(--brand-text, #4a4542)' }}>{cur.body}</Typography>
                    <Box sx={{
                        display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: 14, color: 'var(--brand-text, #6b4a35)', lineHeight: 1.55,
                        backgroundColor: 'var(--brand-surface-muted, #FFF5EE)', borderRadius: '10px', p: '12px 16px',
                    }}>
                        <InfoOutlinedIcon style={{ fontSize: 18, color: ORANGE_DEEP, marginTop: 2 }} />
                        <span>{cur.tip}</span>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mt: '32px', pt: '24px', borderTop: '1px solid var(--brand-border, #f1e9e4)', '@media (max-height: 820px)': { mt: '14px', pt: '14px' } }}>
                    <Box sx={{ flex: 1, display: 'flex', gap: '6px' }}>
                        {steps.map((s, i) => (
                            <Box
                                key={s.title}
                                component="span"
                                title={s.title}
                                onClick={() => setStep(i)}
                                sx={{
                                    height: 8, width: i === step ? 22 : 8, borderRadius: '4px', cursor: 'pointer',
                                    backgroundColor: i === step ? ORANGE : '#F1DCCF',
                                }}
                            />
                        ))}
                    </Box>
                    {step > 0 && (
                        <Button
                            onClick={() => setStep((s) => Math.max(0, s - 1))}
                            sx={{
                                height: 40, px: '20px', borderRadius: '20px', border: '1.5px solid var(--brand-border, #e6dfda)', backgroundColor: 'var(--brand-surface, #fff)',
                                color: 'var(--brand-text, #444)', fontWeight: 500, fontSize: 14, textTransform: 'none',
                            }}
                        >
                            Back
                        </Button>
                    )}
                    <Button
                        onClick={() => (step === last ? closeGuide() : setStep((s) => s + 1))}
                        sx={{
                            height: 40, px: '24px', borderRadius: '20px', backgroundColor: ORANGE, color: '#fff',
                            fontWeight: 700, fontSize: 14, textTransform: 'none', '&:hover': { backgroundColor: ORANGE_DEEP },
                        }}
                    >
                        {step === last ? 'Close' : 'Next'}
                    </Button>
                </Box>
            </Dialog>
        </>
    );
};

export default MySchoolsGuide;
